/**
 * In-app documentation for research indicators.
 * Shown from the Indicators page Help / Docs modal.
 */

export const INDICATOR_DOCS = {
  markov: {
    id: 'markov',
    title: 'Markov Regime',
    category: 'Regime',
    summary:
      'Estimates whether the asset is in a Bull, Bear, or Sideways regime and turns the transition matrix into a signed forward signal.',
    whatItIs: [
      'A regime model based on the observable Markov “hedge fund method” popularized by Roan (@RohOnChain) and the markov-hedge-fund-method open-source companion.',
      'It does not predict the exact next close. It estimates how sticky each regime is and the probability of Bull vs Bear over the next few bars.',
    ],
    howItWorks: [
      'Label every bar Bull / Bear / Sideways from the trailing window return (default 20 bars, ±5%).',
      'Count transitions between those labels to build a 3×3 maximum-likelihood transition matrix.',
      'Read P(next = Bull | current) and P(next = Bear | current). Signal = bull_prob − bear_prob (range about −1 to +1).',
      'Raise the matrix to powers (Chapman–Kolmogorov) to build the forward path for Horizon days.',
      'Also report persistence (diagonal stickiness) and the long-run stationary mix of regimes.',
    ],
    howToRead: [
      'Current regime — label of the latest bar under the rolling-return rule.',
      'Signal > 0 — next-bar probability leans Bull over Bear (long bias). Signal < 0 — Bear lean (short / caution on longs).',
      'Forward path — day-by-day probabilities and signal if the chain evolves from today’s regime.',
      'Long-run Bear share — if high, the name structurally spends more time in drawdowns; size down.',
    ],
    parameters: [
      { name: 'Horizon', meaning: 'How many bars ahead to project (1–30). Default 5.' },
      { name: 'Window', meaning: 'Lookback bars for the rolling return that labels regimes. Default 20.' },
      { name: 'Threshold', meaning: '± return cutoff for Bull/Bear vs Sideways (0.05 = ±5%).' },
    ],
    howToUse: [
      'Standalone: take longs when signal ≥ your entry threshold; flatten when it fades.',
      'As a filter: only take another strategy’s long when Markov signal > 0 (and shorts when < 0).',
      'Backtest / live algos can call helpers.getMarkovRegime(symbol) with the same math.',
    ],
    risks: [
      'Historical transitions are not a guarantee of the future.',
      'Daily bars lag intraday turns; do not treat as a crystal ball.',
      'HMM / hidden-state upgrades from the original Python repo are not included here.',
    ],
    credit: 'Framework: Roan (@RohOnChain). Port inspired by jackson-video-resources/markov-hedge-fund-method (MIT).',
  },

  vol_regime: {
    id: 'vol_regime',
    title: 'Volatility Regime',
    category: 'Risk',
    summary:
      'Measures how “hot” realized volatility is versus its own history, and labels the market high / mid / low vol.',
    whatItIs: [
      'A risk-regime indicator. Quants use vol regimes to decide whether to size up trend trades or cut risk / favor mean reversion.',
      'Uses close-to-close log returns, annualized over a short window, then ranks that level against a longer lookback.',
    ],
    howItWorks: [
      'Compute daily log returns.',
      'Realized vol = standard deviation of the last Window returns × √252 (annualized).',
      'Percentile = where today’s vol sits in the last Lookback observations of that same series.',
      'High if percentile ≥ 70% (default), Low if ≤ 30%, otherwise Mid.',
      'Signal maps percentile to about −1…+1 (low vol → positive / trend-friendly; high vol → negative / caution).',
    ],
    howToRead: [
      'High — elevated risk; prefer smaller size, wider stops, or mean-reversion / hedges.',
      'Low — compressed vol; breakouts and trends historically cleaner, but watch for vol expansion.',
      'Mid — no strong tilt; use other indicators for direction.',
    ],
    parameters: [
      { name: 'Vol window', meaning: 'Bars in the realized-vol estimate (default 20).' },
      { name: 'Lookback', meaning: 'History used for the percentile rank (default 252 ≈ 1y).' },
    ],
    howToUse: [
      'Gate momentum: only full size when vol regime is low or mid.',
      'Scale riskPct × (1 − percentile) so high-vol names automatically trade smaller.',
      'Pair with Markov or TS Momentum — direction from them, size from vol regime.',
    ],
    risks: [
      'Close-to-close vol ignores intraday ranges (Parkinson/GK are richer but need OHLC quality).',
      'A single spike can lift percentile quickly; confirm with ATR stretch if needed.',
      'Low vol can precede violent expansions — do not confuse “quiet” with “safe.”',
    ],
  },

  ts_momentum: {
    id: 'ts_momentum',
    title: 'TS Momentum',
    category: 'Momentum',
    summary:
      'Classic time-series momentum: how the asset has performed over 1, 3, 6, and 12 months, combined into one bias score.',
    whatItIs: [
      'Time-series (absolute) momentum asks: “Has this asset been going up or down over multiple horizons?”',
      'Widely studied in academic finance (e.g. trend-following / AQR-style TSMOM). Different from cross-sectional momentum (rank vs peers).',
    ],
    howItWorks: [
      'Measure simple returns over ~21 / 63 / 126 / 252 trading days (1m / 3m / 6m / 12m).',
      'Optional “skip last month” uses the price from ~21 days ago as the endpoint so the most recent month does not dominate (common research practice).',
      'Average those horizons and squash with a tanh-like scale into a signal in about −1…+1.',
    ],
    howToRead: [
      'Positive legs and positive signal — multi-horizon uptrend; long bias.',
      'Negative — multi-horizon downtrend; short / avoid longs.',
      'Mixed signs across horizons — weak or transitioning trend; stay flat or require confirmation.',
      'The 12m series chart shows how that longer leg evolved recently.',
    ],
    parameters: [
      { name: 'Skip last month', meaning: 'If on, measure returns ending ~21 days ago instead of today.' },
    ],
    howToUse: [
      'Standalone trend sleeve: long when signal > 0.1, flat/short when < −0.1.',
      'Confirm breakouts only when TS momentum agrees.',
      'Needs enough daily history (~280+ bars) for a stable 12m leg.',
    ],
    risks: [
      'Momentum crashes after sharp reversals; combine with vol regime or Markov.',
      'Works best on liquid futures/equity indices; single names can be noisier.',
      'Not a timing tool for intraday scalps — designed for daily/weekly decisions.',
    ],
  },

  mean_reversion_z: {
    id: 'mean_reversion_z',
    title: 'Mean-Reversion Z',
    category: 'Mean reversion',
    summary:
      'How many standard deviations price sits from its moving average (and vs session VWAP when available).',
    whatItIs: [
      'A stretch / mean-reversion meter. Extreme z-scores often precede short-term fades; they are not a reason to fight a strong trend alone.',
      'Z vs SMA uses the rolling SMA window’s own standard deviation. Z vs VWAP uses the same σ scale for distance from session VWAP.',
    ],
    howItWorks: [
      'Compute SMA over smaPeriod and the stdev of closes in that window.',
      'zSma = (close − SMA) / σ.',
      'If |zSma| ≥ entryZ (default 2), flag fade_long (rich) or fade_short (washed out).',
      'Mean-reversion signal is roughly −z / entryZ (fade the extreme).',
    ],
    howToRead: [
      'z ≥ +2 — extended up; classic short/fade candidate if other filters allow.',
      'z ≤ −2 — extended down; classic long mean-reversion candidate.',
      '|z| < entry — no stretch; do not force a fade.',
      'Setup field names the actionable state (fade_long / fade_short / neutral).',
    ],
    parameters: [
      { name: 'SMA period', meaning: 'Lookback for mean and σ (default 20).' },
      { name: 'Entry |z|', meaning: 'Absolute z threshold for a stretch setup (default 2).' },
    ],
    howToUse: [
      'Fade only with a trend or Markov filter (e.g. fade longs only when Markov is not strongly bullish).',
      'On intraday timeframes, watch Z vs VWAP for session mean reversion.',
      'Pair with ATR Stretch — both measure “how far,” in different units.',
    ],
    risks: [
      'Trends can stay extended for a long time (“markets can stay irrational…”).',
      'σ collapses in quiet markets and makes z explode on small moves — check vol regime.',
      'Daily SMA z is a different game from 5-minute VWAP z; match timeframe to intent.',
    ],
  },

  beta_corr: {
    id: 'beta_corr',
    title: 'Beta & Correlation',
    category: 'Risk',
    summary:
      'How much the name moves with a benchmark (default SPY): beta, correlation, and a market-like vs idiosyncratic label.',
    whatItIs: [
      'A risk-characterization tool. High beta + high correlation means the name is mostly equity-market risk; low correlation means stock-specific risk dominates.',
      'Computed on overlapping daily returns vs the benchmark over a rolling window.',
    ],
    howItWorks: [
      'Align asset and benchmark closes by calendar day.',
      'Compute daily returns, then beta = Cov(r_a, r_b) / Var(r_b) and correlation over the last Window days.',
      'Label: market_like (|corr|≥0.7 and beta≥0.8), moderate_beta, defensive_or_inverse (beta<0), or idiosyncratic.',
    ],
    howToRead: [
      'Beta 1 — tends to move 1:1 with the benchmark in this window.',
      'Beta > 1 — amplifies market moves; Beta < 0 — inverse / hedge-like behavior.',
      'Correlation near 0 — Markov and stock-specific signals matter more than “the market.”',
      'Risk label — quick English summary of the above.',
    ],
    parameters: [
      { name: 'Benchmark', meaning: 'Comparison ticker (default SPY). Try QQQ, IWM, or a sector ETF.' },
      { name: 'Window', meaning: 'Return sample length in sessions (default 60).' },
    ],
    howToUse: [
      'Portfolio construction: know whether adding this name doubles SPY exposure.',
      'Strategy filters: idiosyncratic names for stock-picking sleeves; market_like for index overlays.',
      'Hedge sizing: beta helps rough hedge ratios vs the benchmark.',
    ],
    risks: [
      'Beta is unstable — it changes across regimes; re-run after big vol shifts.',
      'Uses close-to-close returns only; overnight gaps dominate some names.',
      'High correlation ≠ causation; both can be driven by a third factor.',
    ],
  },

  sma_stack: {
    id: 'sma_stack',
    title: 'SMA Trend Stack',
    category: 'Trend',
    summary:
      'Classic moving-average stack: price vs a fast and slow SMA, alignment, and golden/death cross flags.',
    whatItIs: [
      'A simple trend filter used everywhere from CTAs to discretionary desks. Not fancy — but transparent and easy to compose.',
      'Default stack is SMA50 / SMA200 (adjustable).',
    ],
    howItWorks: [
      'Compute fast and slow simple moving averages.',
      'Score: + if price above each MA, + if fast > slow (bullish stack); opposite for bearish.',
      'Golden cross — fast crosses above slow on this bar; death cross — fast crosses below slow.',
    ],
    howToRead: [
      'Bullish stack + price above slow MA — long-leaning trend environment.',
      'Bearish stack + price below slow MA — short / risk-off for longs.',
      'Golden / death badges — fresh regime flip; often late but clear.',
      'Chart — close with both MAs overlaid for context.',
    ],
    parameters: [
      { name: 'Fast SMA', meaning: 'Shorter average (default 50).' },
      { name: 'Slow SMA', meaning: 'Longer average (default 200).' },
    ],
    howToUse: [
      'Only take TS Momentum or breakout longs when the stack is bullish.',
      'Use death cross as a hard risk-off for long-only sleeves.',
      'Needs enough history (slow + a few bars); 200-day SMA wants ~1y of daily data.',
    ],
    risks: [
      'Lagging by design — crosses happen after much of the move.',
      'Whipsaws in sideways markets; combine with vol regime or Markov sideways awareness.',
      'SMA length is arbitrary; optimize carefully to avoid curve-fitting.',
    ],
  },

  atr_stretch: {
    id: 'atr_stretch',
    title: 'ATR Stretch',
    category: 'Mean reversion',
    summary:
      'How many Average True Ranges price sits above or below its recent mean — a volatility-normalized stretch meter.',
    whatItIs: [
      'Similar spirit to Mean-Reversion Z, but distance is measured in ATR units instead of close-only σ.',
      'Useful for profit-taking, fade setups, or deciding if a move is “enough” relative to typical range.',
    ],
    howItWorks: [
      'Wilder-style ATR over atrLen from true range (high−low, |high−prev close|, |low−prev close|).',
      'Mean = SMA of closes over meanLen.',
      'stretch = (close − mean) / ATR.',
      'If |stretch| ≥ entryAtr, flag extended_up or extended_down; signal fades the stretch.',
    ],
    howToRead: [
      'stretch ≥ +2 ATR — extended up; consider taking profits on longs or looking for fades.',
      'stretch ≤ −2 ATR — extended down; bounce / cover candidates if filters agree.',
      'Near 0 — price is hugging its mean; wait.',
    ],
    parameters: [
      { name: 'ATR len', meaning: 'ATR lookback (default 14).' },
      { name: 'Mean len', meaning: 'SMA lookback for the center line (default 20).' },
      { name: 'Entry ATR', meaning: 'Absolute stretch threshold (default 2).' },
    ],
    howToUse: [
      'Scale-out rules: trim when stretch exceeds +2 on a winning long.',
      'Fade entries only with a regime filter (do not fade a strong SMA stack blindly).',
      'Compare with Mean-Reversion Z — agree = higher conviction stretch.',
    ],
    risks: [
      'ATR expands in crises and can make stretch look “normal” while price is still extreme in % terms.',
      'Gaps can create one-bar spikes in true range.',
      'Not a standalone alpha engine — best as a timing / risk overlay.',
    ],
  },
};

export function listIndicatorDocs() {
  return Object.values(INDICATOR_DOCS);
}

export function getIndicatorDoc(id) {
  return INDICATOR_DOCS[id] || null;
}
